import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { HardHat, ShieldCheck, Frame, BadgeCheck, Images, X, ChevronLeft, ChevronRight, Baby, Home as HomeIcon, Layers, ArrowRight, Camera } from "lucide-react";
import { useWizard } from "./WizardProvider";

const RED = "#FF1744";
const GREEN = "#00E676";

// Real WIP site photography — Regional NSW Early Learning & Childcare Center.
const CHILDCARE_PHOTOS = [
  { src: "/projects/childcare/cc-05.jpg", caption: "Site Wide-Angle — Front Elevation & Streetscape" },
  { src: "/projects/childcare/cc-04.jpg", caption: "Structural Steel Wall & Roof Truss Assembly" },
  { src: "/projects/childcare/cc-02.jpg", caption: "Slab Preparation & Ground Drainage Integration" },
  { src: "/projects/childcare/cc-01.jpg", caption: "Precision LGS Steel Frame Elevation" },
  { src: "/projects/childcare/cc-03.jpg", caption: "Perimeter Fencing & Site Safety Setup" },
];

const CHILDCARE_HIGHLIGHTS = [
  { icon: Frame, text: "Custom Light-Gauge Steel (LGS) Precision Framing" },
  { icon: ShieldCheck, text: "Non-Combustible & Termite-Proof Commercial Structure" },
  { icon: BadgeCheck, text: "Engineered for High-Occupancy Commercial Compliance & Long-Term Yield" },
];

const CHILDCARE_PHASES = ["Bulk Earthworks", "Slab & Drainage", "Structural Framing", "Lock-Up", "Fit-Out", "Handover"];
const CHILDCARE_ACTIVE_PHASE = 2; // Structural Framing

// Real WIP site photography — residential custom home / KDR pipeline.
const RES_STAGES = [
  { id: 1, label: "Earthworks & Slab Preparation" },
  { id: 2, label: "Wall Framing & Structural Roof Trusses" },
  { id: 3, label: "Services Rough-In & Lock-Up Phase" },
];

const RES_PHOTOS = [
  { src: "/projects/residential/rh-05.jpg", stage: 1, caption: "Full Site Overview — Waffle Pod Slab & Reo Mesh Laid" },
  { src: "/projects/residential/rh-04.jpg", stage: 1, caption: "Slab Formwork & Edge Boards — Pour-Ready" },
  { src: "/projects/residential/rh-02.jpg", stage: 1, caption: "Steel Reinforcement Mesh Across Full Footprint" },
  { src: "/projects/residential/rh-01.jpg", stage: 1, caption: "Waffle Pod Detail — Mesh Chairs & Moisture Barrier" },
  { src: "/projects/residential/rh-03.jpg", stage: 3, caption: "Under-Slab Service Penetrations & Drainage Rough-In" },
];

const RES_TABS = [{ id: "all", label: "All Photos" }, ...RES_STAGES];

const RES_HIGHLIGHTS = [
  { icon: Frame, text: "Custom Timber Framing & Roof Truss Installation" },
  { icon: Layers, text: "Slab & Subfloor Drainage Integration" },
  { icon: BadgeCheck, text: "Engineered for NSW Building Standards & Energy Efficiency" },
];

const SiteLightbox = ({ title, photos, tabs, accent, startTab, startIndex, onClose }) => {
  const [tab, setTab] = useState(startTab);
  const [index, setIndex] = useState(startIndex);

  const filtered = useMemo(
    () => (tabs && tab !== "all" ? photos.filter((p) => p.stage === tab) : photos),
    [tabs, tab, photos]
  );

  const nav = useCallback(
    (delta) => setIndex((i) => (filtered.length ? (i + delta + filtered.length) % filtered.length : 0)),
    [filtered.length]
  );

  const firstTab = useRef(tab);
  useEffect(() => {
    if (firstTab.current === tab) return;
    firstTab.current = tab;
    setIndex(0);
  }, [tab]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") nav(1);
      if (e.key === "ArrowLeft") nav(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, nav]);

  const current = filtered[index];

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[96] bg-[#0A0E17]/95 backdrop-blur-md flex flex-col"
      onClick={onClose}
      data-testid="site-lightbox"
    >
      <div className="flex items-center justify-between gap-4 px-5 sm:px-8 py-4 shrink-0">
        <div className="min-w-0">
          <p className="font-display text-sm font-bold text-white truncate">{title}</p>
          <p className="text-[11px] text-white/50 truncate" data-testid="site-lightbox-counter">
            {current ? `Photo ${index + 1} of ${filtered.length} — ${current.caption}` : "No photos in this stage yet"}
          </p>
        </div>
        <button
          onClick={onClose}
          data-testid="site-lightbox-close"
          aria-label="Close gallery"
          className="w-10 h-10 shrink-0 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:text-white transition-colors duration-300"
        >
          <X size={18} />
        </button>
      </div>

      {tabs && (
        <div className="shrink-0 px-5 sm:px-8 pb-3 flex gap-2 overflow-x-auto" onClick={(e) => e.stopPropagation()} data-testid="site-lightbox-stages">
          {tabs.map((t) => {
            const active = tab === t.id;
            const count = t.id === "all" ? photos.length : photos.filter((p) => p.stage === t.id).length;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`site-stage-tab-${t.id}`}
                className={`shrink-0 rounded-full px-4 py-2 font-display text-[11px] font-bold tracking-wide border transition-all duration-300 ${
                  active ? "text-white" : "border-white/10 text-white/50 hover:border-white/30"
                }`}
                style={active ? { borderColor: accent, background: `${accent}22`, boxShadow: `0 0 16px ${accent}44` } : {}}
              >
                {t.id === "all" ? t.label : `Stage ${t.id} — ${t.label}`}
                <span className="ml-1.5 opacity-60">{count}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex-1 relative flex items-center justify-center px-4 sm:px-16 min-h-0" onClick={(e) => e.stopPropagation()}>
        {current ? (
          <>
            <AnimatePresence mode="wait">
              <motion.img
                key={`${tab}-${current.src}`}
                src={current.src}
                alt={current.caption}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.25 }}
                className="max-h-full max-w-full object-contain rounded-xl shadow-[0_30px_100px_rgba(0,0,0,0.7)]"
                data-testid="site-lightbox-img"
              />
            </AnimatePresence>
            <button
              onClick={() => nav(-1)}
              data-testid="site-lightbox-prev"
              aria-label="Previous photo"
              className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full glass border border-white/20 flex items-center justify-center text-white/80 hover:text-white hover:scale-110 transition-all duration-300"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => nav(1)}
              data-testid="site-lightbox-next"
              aria-label="Next photo"
              className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full glass border border-white/20 flex items-center justify-center text-white/80 hover:text-white hover:scale-110 transition-all duration-300"
            >
              <ChevronRight size={20} />
            </button>
          </>
        ) : (
          <div className="text-center px-8 py-16 rounded-2xl border border-dashed border-white/15 max-w-md" data-testid="site-stage-empty">
            <Camera size={34} style={{ color: accent }} className="mx-auto mb-4" />
            <p className="font-display text-base font-bold text-white">Framing underway on site</p>
            <p className="mt-2 text-xs text-white/50 leading-relaxed">
              This stage is live on the tools right now — progress photos drop here with the next site update.
            </p>
          </div>
        )}
      </div>

      {filtered.length > 0 && (
        <div className="shrink-0 px-5 sm:px-8 pb-5 pt-3 overflow-x-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex gap-2 justify-center">
            {filtered.map((p, i) => (
              <button
                key={p.src}
                onClick={() => setIndex(i)}
                data-testid={`site-lightbox-thumb-${i}`}
                aria-label={`Go to photo ${i + 1}`}
                className={`shrink-0 w-14 h-10 rounded-md overflow-hidden border-2 transition-all duration-300 ${
                  i === index ? "" : "border-white/15 opacity-50 hover:opacity-90"
                }`}
                style={i === index ? { borderColor: accent, boxShadow: `0 0 12px ${accent}88` } : {}}
              >
                <img src={p.src} alt="" loading="lazy" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </motion.div>,
    document.body
  );
};

const PhaseTracker = ({ phases, active, accent, doneLabel, activeLabel, todoLabel, testId }) => (
  <div data-testid={testId}>
    <div className="flex items-center gap-1.5">
      {phases.map((phase, i) => (
        <span
          key={phase}
          className="h-1.5 flex-1 rounded-full transition-colors duration-500"
          style={{
            background: i < active ? "#00E676" : i === active ? accent : "rgba(255,255,255,0.12)",
            boxShadow: i === active ? `0 0 12px ${accent}` : "none",
          }}
        />
      ))}
    </div>
    <div className="mt-2.5 flex justify-between text-[9px] font-display font-bold tracking-wider uppercase">
      <span className="text-[#00E676]">{doneLabel}</span>
      <span style={{ color: accent }}>{activeLabel}</span>
      <span className="text-white/35">{todoLabel}</span>
    </div>
  </div>
);

const ThumbRow = ({ photos, onOpen, testId }) => (
  <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-2" data-testid={testId}>
    {photos.map((p, i) => (
      <button key={p.src} onClick={() => onOpen(i)} data-testid={`${testId}-${i}`} aria-label={p.caption} className="group/thumb text-left">
        <span className="block h-24 sm:h-20 rounded-lg overflow-hidden border border-white/10 transition-colors duration-300">
          <img src={p.src} alt={p.caption} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover/thumb:scale-110" />
        </span>
      </button>
    ))}
  </div>
);

const ChildcareCard = ({ onOpen }) => {
  const { openWizard } = useWizard();
  return (
    <motion.article
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7 }}
      className="group overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] hover:border-[#FF1744]/40 transition-colors duration-300"
      data-testid="wip-card-childcare"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="relative h-72 lg:h-auto overflow-hidden cursor-pointer" onClick={() => onOpen(0)}>
          <img
            src={CHILDCARE_PHOTOS[0].src}
            alt="Regional NSW Early Learning & Childcare Center — LGS structural framing, front site view"
            loading="lazy"
            data-testid="wip-cover-childcare"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E17] via-transparent to-transparent pointer-events-none" />
          <span
            className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full px-4 py-2 font-display text-[10px] font-bold tracking-[0.18em] text-white bg-[#FF1744] shadow-[0_0_20px_rgba(255,23,68,0.5)]"
            data-testid="wip-badge-childcare"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
            </span>
            ACTIVE CONSTRUCTION — STRUCTURAL FRAMING PHASE
          </span>
          <span className="absolute bottom-4 left-4 max-w-[62%] rounded-full glass border border-white/15 px-3.5 py-1.5 font-display text-[10px] font-bold tracking-wide text-white/85 pointer-events-none" data-testid="wip-caption-childcare">
            NSW Regional Early Learning Centre — Active LGS Steel Framing Phase
          </span>
          <span className="absolute bottom-4 right-4 rounded-full glass border border-white/20 px-3.5 py-1.5 font-display text-[11px] font-bold text-white inline-flex items-center gap-1.5" data-testid="wip-photocount-childcare">
            <Images size={13} style={{ color: RED }} /> {CHILDCARE_PHOTOS.length} Site Photos
          </span>
        </div>

        <div className="p-7 sm:p-9 border-t-2 lg:border-t-0 lg:border-l-2" style={{ borderColor: RED }}>
          <p className="font-display text-[10px] font-bold tracking-[0.2em] mb-2" style={{ color: RED }} data-testid="wip-devtype-childcare">
            SPECIALIZED COMMERCIAL ASSET / HIGH-YIELD CHILDCARE FACILITY
          </p>
          <h3 className="font-display font-bold text-2xl sm:text-3xl text-white tracking-tight" data-testid="wip-title-childcare">
            Regional NSW Early Learning &amp; Childcare Center
          </h3>
          <p className="mt-2 text-xs text-white/50 leading-relaxed" data-testid="wip-scope-childcare">
            Complete turn-key commercial build — from bulk earthworks to internal fit-out.
          </p>

          <div className="mt-6">
            <PhaseTracker
              phases={CHILDCARE_PHASES}
              active={CHILDCARE_ACTIVE_PHASE}
              accent={RED}
              doneLabel="Earthworks & Slab ✓"
              activeLabel="Structural Framing — Current"
              todoLabel="Fit-Out → Handover"
              testId="wip-phases-childcare"
            />
          </div>

          <ul className="mt-6 space-y-2.5">
            {CHILDCARE_HIGHLIGHTS.map((h) => (
              <li key={h.text} className="flex items-start gap-3 text-xs text-white/70 leading-relaxed">
                <h.icon size={15} style={{ color: RED }} className="shrink-0 mt-0.5" />
                {h.text}
              </li>
            ))}
          </ul>

          <ThumbRow photos={CHILDCARE_PHOTOS.slice(1)} onOpen={(i) => onOpen(i + 1)} testId="wip-thumb-childcare" />

          <button
            onClick={() => openWizard("Childcare Centres")}
            data-testid="wip-cta-childcare"
            className="mt-7 inline-flex items-center gap-2 rounded-full px-6 py-3 font-display text-sm font-bold text-white bg-gradient-to-r from-[#FF3D00] to-[#FF1744] hover:shadow-[0_0_26px_rgba(255,23,68,0.5)] hover:scale-[1.03] transition-all duration-300"
          >
            <Baby size={16} /> Inquire on Childcare Yield Builds
          </button>
        </div>
      </div>
    </motion.article>
  );
};

const ResidentialCard = ({ onOpen }) => {
  const lenis = useLenis();
  const scrollToWizard = () => {
    if (lenis) lenis.scrollTo("#contact", { offset: -110, duration: 1.4 });
    else document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7 }}
      className="group overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] hover:border-[#00E676]/40 transition-colors duration-300"
      data-testid="wip-card-residential"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="relative h-72 lg:h-auto overflow-hidden cursor-pointer" onClick={() => onOpen("all", 0)}>
          <img
            src={RES_PHOTOS[0].src}
            alt="Active residential home build — waffle pod slab and reinforcement mesh, NSW site"
            loading="lazy"
            data-testid="wip-cover-residential"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E17] via-transparent to-transparent pointer-events-none" />
          <span
            className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full px-4 py-2 font-display text-[10px] font-bold tracking-[0.18em] text-[#0A0E17] bg-[#00E676] shadow-[0_0_20px_rgba(0,230,118,0.5)]"
            data-testid="wip-badge-residential"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0A0E17] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#0A0E17]" />
            </span>
            IN CONSTRUCTION — FRAMING &amp; STRUCTURAL PHASE
          </span>
          <span className="absolute bottom-4 left-4 max-w-[62%] rounded-full glass border border-white/15 px-3.5 py-1.5 font-display text-[10px] font-bold tracking-wide text-white/85 pointer-events-none" data-testid="wip-caption-residential">
            Custom Family Home — Active Structural Framing &amp; Roof Truss Phase
          </span>
          <span className="absolute bottom-4 right-4 rounded-full glass border border-white/20 px-3.5 py-1.5 font-display text-[11px] font-bold text-white inline-flex items-center gap-1.5" data-testid="wip-photocount-residential">
            <Images size={13} style={{ color: GREEN }} /> {RES_PHOTOS.length} Site Photos
          </span>
        </div>

        <div className="p-7 sm:p-9 border-t-2 lg:border-t-0 lg:border-l-2" style={{ borderColor: GREEN }}>
          <p className="font-display text-[10px] font-bold tracking-[0.2em] mb-2" style={{ color: GREEN }} data-testid="wip-kicker-residential">
            ACTIVE RESIDENTIAL HOME BUILDS — NSW SITE PROGRESS
          </p>
          <h3 className="font-display font-bold text-2xl sm:text-3xl text-white tracking-tight" data-testid="wip-title-residential">
            Custom Family Homes &amp; Knock-Down Rebuilds
          </h3>
          <p className="mt-2 text-xs text-white/50 leading-relaxed" data-testid="wip-desc-residential">
            Real-time job site progress across custom residential family homes and knock-down-rebuild projects in NSW.
          </p>

          <div className="mt-6 flex flex-wrap gap-2" data-testid="wip-stages-residential">
            {RES_STAGES.map((s) => {
              const count = RES_PHOTOS.filter((p) => p.stage === s.id).length;
              return (
                <button
                  key={s.id}
                  onClick={() => onOpen(s.id, 0)}
                  data-testid={`wip-stage-pill-${s.id}`}
                  className="rounded-full border border-[#00E676]/30 bg-[#00E676]/[0.06] px-3.5 py-1.5 font-display text-[10px] font-bold tracking-wide text-white/70 hover:text-white hover:border-[#00E676]/60 hover:shadow-[0_0_14px_rgba(0,230,118,0.3)] transition-all duration-300"
                >
                  Stage {s.id} · {s.label} <span style={{ color: GREEN }}>— {count} photo{count === 1 ? "" : "s"}</span>
                </button>
              );
            })}
          </div>

          <ul className="mt-6 space-y-2.5">
            {RES_HIGHLIGHTS.map((h) => (
              <li key={h.text} className="flex items-start gap-3 text-xs text-white/70 leading-relaxed">
                <h.icon size={15} style={{ color: GREEN }} className="shrink-0 mt-0.5" />
                {h.text}
              </li>
            ))}
          </ul>

          <ThumbRow photos={RES_PHOTOS.slice(1)} onOpen={(i) => onOpen("all", i + 1)} testId="wip-thumb-residential" />

          <button
            onClick={scrollToWizard}
            data-testid="wip-cta-residential"
            className="mt-7 inline-flex items-center gap-2 rounded-full px-6 py-3 font-display text-sm font-bold text-[#0A0E17] bg-[#00E676] hover:shadow-[0_0_26px_rgba(0,230,118,0.5)] hover:scale-[1.03] transition-all duration-300"
          >
            Start Your Residential Build Query <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </motion.article>
  );
};

const TABS = [
  { id: "commercial", label: "Commercial & Yield Projects", icon: HardHat, accent: RED },
  { id: "residential", label: "Residential Home Builds", icon: HomeIcon, accent: GREEN },
];

export const ActiveProjects = () => {
  const [tab, setTab] = useState("commercial");
  const [lightbox, setLightbox] = useState(null); // { kind, tab, index }

  return (
    <section id="active-projects" className="px-6 sm:px-12 lg:px-20 py-28 lg:py-36 bg-[#0A0E17]" data-testid="active-projects-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-10"
        >
          <p className="font-display text-[11px] font-bold tracking-[0.35em] text-[#FF1744] mb-4 flex items-center gap-3">
            <HardHat size={14} /> ACTIVE CONSTRUCTION — NSW SITE PROGRESS
          </p>
          <h2 className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl text-white leading-[1.02] max-w-3xl">
            On the tools.{" "}
            <em className="font-serif-lux italic font-semibold text-gradient-fire">Right now.</em>
          </h2>
          <p className="mt-5 max-w-2xl text-sm sm:text-base text-white/60 leading-relaxed">
            Live progress from our current pipeline — specialized commercial assets and custom
            residential family homes across NSW growth corridors.
          </p>
        </motion.div>

        <div className="mb-8 flex flex-wrap gap-2" data-testid="active-projects-tabs">
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`active-tab-${t.id}`}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-display text-xs font-bold tracking-wide border transition-all duration-300 ${
                  active ? "text-white" : "border-white/10 text-white/50 hover:border-white/30"
                }`}
                style={active ? { borderColor: t.accent, background: `${t.accent}1f`, boxShadow: `0 0 18px ${t.accent}44` } : {}}
              >
                <t.icon size={14} style={{ color: active ? t.accent : "rgba(255,255,255,0.5)" }} />
                {t.label}
              </button>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35 }}
          >
            {tab === "commercial" ? (
              <ChildcareCard onOpen={(index) => setLightbox({ kind: "commercial", tab: "all", index })} />
            ) : (
              <ResidentialCard onOpen={(stageTab, index) => setLightbox({ kind: "residential", tab: stageTab, index })} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {lightbox?.kind === "commercial" && (
          <SiteLightbox
            title="Regional NSW Early Learning & Childcare Center"
            photos={CHILDCARE_PHOTOS}
            tabs={null}
            accent={RED}
            startTab="all"
            startIndex={lightbox.index}
            onClose={() => setLightbox(null)}
          />
        )}
        {lightbox?.kind === "residential" && (
          <SiteLightbox
            title="Active Residential Home Builds — NSW Site Progress"
            photos={RES_PHOTOS}
            tabs={RES_TABS}
            accent={GREEN}
            startTab={lightbox.tab}
            startIndex={lightbox.index}
            onClose={() => setLightbox(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};
